/**
 * Stapel afleveringen die je opzij kunt schuiven.
 *
 * Opgezet naar het voorbeeld van Osmo (stacked cards): je sleept de voorste kaart zelf,
 * bij loslaten beslist de afgelegde afstand wat er gebeurt, en de weggetikte kaart gaat
 * achteraan de stapel. Het rondje ontstaat door de kaart in de DOM naar achteren te
 * verplaatsen; de plek in de stapel is verder puur een transform.
 *
 * Waar het voorbeeld de kaarten met een paar graden draait, gebruiken wij de waaier uit
 * het ontwerp: de tweede staat 6,73% naar rechts op 93,7%, de derde 13,37% op 88,3%.
 * Alles daarachter ligt op die derde plek maar onzichtbaar.
 *
 * De weggetikte kaart komt niet ter plekke tot stilstand: hij glijdt eerst het beeld uit
 * en veert daarna achter de stapel langs terug naar achteren. Dat laatste stuk zie je
 * grotendeels niet -- precies de bedoeling, hij hoort tussen de andere kaarten te duiken.
 */

import gsap from 'gsap';
import Draggable from 'gsap/Draggable';

gsap.registerPlugin(Draggable);

// Snel eruit, langzaam en verend terug -- de twee bewegingen uit het voorbeeld.
const EASE_OUT = { duration: 0.28, ease: 'power2.out' };
const EASE_BACK = { duration: 1, ease: 'elastic.out(1, 0.75)' };

// Hoe ver je moet slepen voordat het als wegtikken telt, en hoe ver de kaart het beeld
// uit gaat. Allebei als deel van de kaartbreedte.
const THRESHOLD = 0.1;
const EXIT = 1.15;

export default function initEpisodes() {
  document.querySelectorAll('[data-episodes]').forEach(setup);
}

function setup(deck) {
  const stack = deck.querySelector('[data-episodes-stack]');
  const items = stack ? Array.from(stack.querySelectorAll('[data-episodes-item]')) : [];
  const total = items.length;

  if (! total) {
    return;
  }

  const progress = deck.querySelector('[data-episodes-progress]');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let seen = 0;
  let dragger = null;
  let busy = false;

  restack(true);

  // Eén kaart is geen stapel.
  if (total < 2) {
    return;
  }

  deck.querySelector('[data-episodes-prev]')?.addEventListener('click', () => back());
  deck.querySelector('[data-episodes-next]')?.addEventListener('click', () => flick('left'));

  /**
   * De stand van een kaart, naar zijn plek in de stapel gerekend.
   */
  function slot(position) {
    switch (position) {
      case 0:
        return { x: 0, scale: 1, opacity: 1, z: total + 2 };
      case 1:
        return { x: 6.73, scale: 0.93663, opacity: 1, z: total + 1 };
      case 2:
        return { x: 13.37, scale: 0.88317, opacity: 1, z: total };
      default:
        return { x: 13.37, scale: 0.88317, opacity: 0, z: total - position };
    }
  }

  /**
   * Zet elke kaart op de plek die bij zijn positie in de DOM hoort.
   */
  function restack(immediate, skip) {
    const order = Array.from(stack.querySelectorAll('[data-episodes-item]'));

    order.forEach((item, position) => {
      const card = item.querySelector('.episodes__card');
      const state = slot(position);
      const front = position === 0;

      item.setAttribute('data-episode-status', front ? 'active' : (position < 3 ? String(position + 1) : 'hidden'));
      item.style.zIndex = String(state.z);
      item.style.pointerEvents = front ? 'auto' : 'none';

      // Alleen de voorste kaart doet mee met tabben: anders spring je naar een knop die
      // achter een andere kaart ligt.
      item.setAttribute('aria-hidden', front ? 'false' : 'true');
      item.querySelectorAll('a, button').forEach((element) => {
        element.tabIndex = front ? 0 : -1;
      });

      // De kaart die net is weggetikt houdt zijn eigen x-tween; die veert elastisch
      // terug en mag hier niet tegelijk naar nul getrokken worden.
      const tween = {
        duration: immediate || reduced ? 0 : 0.45,
        ease: 'power3.out',
        xPercent: state.x,
        scale: state.scale,
        opacity: state.opacity,
        overwrite: 'auto',
      };

      if (card !== skip) {
        tween.x = 0;
      }

      gsap.to(card, tween);
    });

    if (progress) {
      gsap.to(progress, {
        scaleX: ((seen % total) + 1) / total,
        duration: immediate || reduced ? 0 : 0.45,
        ease: 'power3.out',
      });
    }

    grab();
  }

  /**
   * De voorste kaart sleepbaar maken; de vorige instantie gaat weg.
   */
  function grab() {
    dragger?.kill();

    if (reduced) {
      return;
    }

    const front = stack.querySelector('[data-episodes-item]');
    const card = front.querySelector('.episodes__card');
    const width = card.getBoundingClientRect().width || 1;
    const exit = width * EXIT;

    dragger = Draggable.create(card, {
      type: 'x',
      cursor: 'grab',
      activeCursor: 'grabbing',
      allowNativeTouchScrolling: true,

      onDrag() {
        // Voorbij de uitgang wordt het stroperig, zodat je merkt dat er niet meer komt.
        let x = this.x;

        if (Math.abs(x) > exit) {
          x = Math.sign(x) * (exit + (Math.abs(x) - exit) * 0.1);
        }

        gsap.set(card, { x });
      },

      onDragEnd() {
        const travelled = Math.abs(this.x);

        if (travelled <= width * THRESHOLD) {
          // Te weinig: gewoon terug op zijn plek.
          gsap.to(card, { x: 0, ...EASE_OUT });

          return;
        }

        flick(this.x < 0 ? 'left' : 'right', this.x);
      },
    })[0];
  }

  /**
   * De voorste kaart het beeld uit tikken en achteraan de stapel zetten.
   */
  function flick(direction, from = 0) {
    if (busy) {
      return;
    }

    busy = true;
    dragger?.disable();

    const item = stack.querySelector('[data-episodes-item]');
    const card = item.querySelector('.episodes__card');
    const width = card.getBoundingClientRect().width || 1;
    const exit = (direction === 'right' ? 1 : -1) * width * EXIT;

    seen += 1;

    const settle = () => {
      // Achteraan in de DOM: daarmee schuift de hele stapel een plek op.
      stack.appendChild(item);
      restack(false, card);

      // De kaart veert vanaf de uitgang terug naar zijn plek achterin. Dat gaat achter de
      // andere kaarten langs, dus je ziet er net genoeg van om te snappen waar hij bleef.
      gsap.fromTo(
        card,
        { x: exit },
        {
          x: 0,
          ...(reduced ? { duration: 0 } : EASE_BACK),
          onComplete() {
            busy = false;
          },
        }
      );
    };

    if (reduced) {
      settle();

      return;
    }

    gsap.fromTo(card, { x: from }, { x: exit, ...EASE_OUT, onComplete: settle });
  }

  /**
   * Terug: de achterste kaart komt weer vooraan en glijdt vanaf de zijkant in beeld.
   */
  function back() {
    if (busy) {
      return;
    }

    busy = true;
    dragger?.disable();

    const order = Array.from(stack.querySelectorAll('[data-episodes-item]'));
    const item = order[order.length - 1];
    const card = item.querySelector('.episodes__card');
    const width = card.getBoundingClientRect().width || 1;

    seen = seen > 0 ? seen - 1 : total - 1;

    stack.insertBefore(item, stack.firstChild);
    restack(false, card);

    gsap.fromTo(
      card,
      { x: -width * EXIT },
      {
        x: 0,
        ...(reduced ? { duration: 0 } : EASE_BACK),
        onComplete() {
          busy = false;
        },
      }
    );
  }
}
